/**
 * InAppNotificationDialog
 * =======================
 * The full view of an admin-authored in-app notification, opened from the bell
 * dropdown or the /home/notifications list.
 *
 * Why a dialog and not a page: the bell row and the list row both render the
 * flattened one-line `message` preview (they have to — every other notification
 * type is plain text and the row layout is shared). The Markdown body and the
 * media attachments need somewhere to actually be shown, and this is it. Without
 * it the rich half of the payload would be written, delivered, and never seen.
 *
 * Clicking an in-app announcement therefore opens this instead of navigating.
 * The announcement's own `actionUrl` is the navigation — it is an explicit button
 * here, so a user reads the message first and then chooses to follow it.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, Megaphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import { InAppNotificationContent } from './InAppNotificationContent';
import {
  InAppAnnouncement,
  InAppSeverity,
  SEVERITY_PRESENTATION,
  isInAppAnnouncementExpired,
  isInternalInAppActionUrl,
  isSafeInAppActionUrl,
} from '@/lib/notifications/inAppNotification';

/** Icon per severity. `warning` and `critical` share the shape; colour separates them. */
const SEVERITY_ICON: Record<InAppSeverity, React.ComponentType<{ className?: string }>> = {
  info: Megaphone,
  success: CheckCircle2,
  warning: AlertTriangle,
  critical: AlertTriangle,
};

export interface InAppNotificationDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  createdAt?: string | null;
  announcement: InAppAnnouncement | null;
}

/** Relative time, or '' when the timestamp is missing/unparseable. */
function relativeTime(createdAt: string | null | undefined): string {
  if (!createdAt) return '';
  const parsed = new Date(createdAt);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDistanceToNow(parsed, { addSuffix: true });
}

export const InAppNotificationDialog: React.FC<InAppNotificationDialogProps> = ({
  open,
  onClose,
  title,
  createdAt,
  announcement,
}) => {
  const navigate = useNavigate();

  if (!announcement) return null;

  const showAction = isSafeInAppActionUrl(announcement.actionUrl);

  const handleAction = () => {
    const url = announcement.actionUrl;
    if (!isSafeInAppActionUrl(url)) {
      onClose();
      return;
    }
    if (isInternalInAppActionUrl(url)) {
      navigate(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  const sentAgo = relativeTime(announcement.sentAt || createdAt);
  const severity = SEVERITY_PRESENTATION[announcement.severity];
  const SeverityIcon = SEVERITY_ICON[announcement.severity];
  // An expired announcement is still READABLE here — the row is in the user's
  // bell and hiding its contents on click would read as a broken notification.
  // What expiry changes is that it stops being OFFERED (no toast, no modal) and
  // says so, because the action it points at may no longer be valid.
  const expired = isInAppAnnouncementExpired(announcement);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="flex max-h-[85vh] max-w-lg flex-col gap-0 p-0"
        data-testid="in-app-notification-dialog"
      >
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${severity.iconWrapClass}`}
              data-testid={`in-app-notification-severity-${announcement.severity}`}
            >
              <SeverityIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <DialogTitle className="text-left text-base leading-snug">{title}</DialogTitle>
                {/* The `info` default carries no useful information — every
                    announcement would wear the same chip — so it is left off and
                    the icon colour is the only cue. */}
                {announcement.severity !== 'info' ? (
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${severity.chipClass}`}
                  >
                    {severity.label}
                  </span>
                ) : null}
              </div>
              <DialogDescription className="mt-1 text-left text-xs text-muted-foreground">
                {[announcement.sentByEmail, sentAgo].filter(Boolean).join(' · ')}
              </DialogDescription>
              {expired ? (
                <p
                  className="mt-1.5 flex items-center gap-1.5 text-left text-xs text-muted-foreground"
                  data-testid="in-app-notification-expired"
                >
                  <Clock className="h-3 w-3 shrink-0" />
                  This announcement has expired
                </p>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        {/* The body scrolls, the header and footer stay put — an announcement
            with several attachments is easily taller than the viewport. */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
          {/* An internal action button navigates with the router and closes the
              dialog, rather than doing a full page load that would drop the
              user's place in the app. External ones open in a new tab from the
              component itself. */}
          <InAppNotificationContent
            announcement={announcement}
            onNavigate={(url) => {
              navigate(url);
              onClose();
            }}
          />
        </div>

        <DialogFooter className="gap-2 border-t px-6 py-4 sm:justify-end">
          <Button variant={showAction ? 'outline' : 'default'} onClick={onClose}>
            Close
          </Button>
          {showAction ? (
            <Button onClick={handleAction} data-testid="in-app-notification-action">
              {announcement.actionLabel || 'View'}
              {isInternalInAppActionUrl(announcement.actionUrl) ? null : (
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              )}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InAppNotificationDialog;
